import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { NativeSelect } from "@mantine/core";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Person } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PAGE_SIZE, applySorts, useListParams } from "../lib/useListParams";
import { ListShell } from "../components/ListShell";
import { EnrichmentBadge, useEnrichmentForIds } from "../components/Enrichment";
import { RecordLink } from "../components/RecordLink";
import { absent, nothingUseful, orAbsent } from "../lib/format";
import { recallListPlace, rememberListPlace } from "../lib/listMemory";
import { downloadNarrowed } from "../lib/csvDownload";
import { applyRules } from "../lib/advancedFilters";
import { useCsvColumns } from "../lib/columnChoice";
import { RowActionForm, type RowAction } from "../components/RowActions";
import { DataTable, visibleWith, type ColumnSpec } from "../ui/DataTable";
import { RowMenu } from "../ui/Menu";
import { FunctionWord, LEVEL_WORDS, StandingWord, levelWord, reachWordsFrom } from "../ui/words";

const FILTERS = ["level", "reach", "hubspot"] as const;

const COLUMNS =
  "id,full_name,title,level,job_function,employer_id,employer_name,standing,has_mobile,has_email,has_phone,has_linkedin,source_count,newest_source_date,hubspot_status";

/* The fields a rep wants first. Everything else the view returns can be
   added from the Columns control, and the choice is remembered. */
const CSV_DEFAULT = ["full_name", "title", "level", "job_function", "employer_name", "standing",
             "has_mobile", "has_email", "has_phone", "source_count", "hubspot_status"] as const;

export function People({ session }: { session: SignedIn }) {
  /* The people the engine knows most about, first.
     James asked for this on 2026-09-15 and called it a good enough proxy for
     V1: it puts real people on the first screen instead of whoever is
     alphabetically first.
     It was tried earlier the same day and reverted, because api.person derived
     source_count in a lateral over signal_link and ordering by it took 15.63
     seconds for a page of fifty against 0.01 for the name. Engine migrations
     080 and 082 store the count and put it on the row behind a partial index,
     so the planner walks the index, takes fifty rows and runs the view's
     laterals for those fifty. Measured warm afterwards: 0.03s.
     full_name is the tiebreak, in both this query and the index, because most
     people sit at one source and the ties are most of the list. */
  const params = useListParams("source_count", FILTERS, false, "person");
  const csvColumns = useCsvColumns("people", "person", CSV_DEFAULT);
  const [rowOpen, setRowOpen] = useState<{ id: string | null; action: RowAction }>({ id: null, action: null });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  /* A record is a page. Opening one remembers where this list was, so Back
     returns to the same scroll position with the opened row marked. */
  const placeKey = `people${location.search}`;
  const [lastOpened, setLastOpened] = useState<string | null>(null);
  const restored = useRef(false);

  const narrowed = (opts?: { count?: "exact"; head?: boolean }, columns?: string) => {
    /* A search reads api.person_search, which matches the full name or any other name
       the record is known by (engine migration 121), and returns rows of the
       same view, so every filter, sort, page and count below works unchanged. */
    const s = params.search.trim();
    const client = apiClient(session.idToken);
    type Rows = ReturnType<ReturnType<typeof client.from>["select"]>;
    let q = s
      ? (client.rpc("person_search", { p_q: s }, { get: true, ...opts }) as unknown as Rows)
      : client.from("person").select(columns ?? COLUMNS, opts);
    if (params.filters["level"]) q = q.eq("level", params.filters["level"]);
    if (params.filters["hubspot"]) q = q.eq("hubspot_status", params.filters["hubspot"]);
    // "Thin" is the word the engine uses for a record with no way to reach
    // somebody. It is what decides whether enrichment is worth running.
    if (params.filters["reach"] === "none") {
      q = q.eq("has_mobile", false).eq("has_email", false).eq("has_phone", false);
    }
    if (params.filters["reach"] === "mobile") q = q.eq("has_mobile", true);
    q = applyRules(q, "person", params.advanced);
    return s ? (q as unknown as { select: (c: string) => Rows }).select(columns ?? COLUMNS) : q;
  };

  const query = useQuery({
    queryKey: ["people", params.page, params.search, params.sorts, params.filters, params.advanced],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error } = await applySorts(narrowed(), params.sorts)
        // Most people sit at one source, so almost the whole list ties. Without a
        // last key Postgres may order the ties differently per request, so a
        // row can appear on two pages and another on none.
        .order("full_name", { ascending: true })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as Person[];
    },
  });

  const countQuery = useQuery({
    queryKey: ["people", "count", params.search, params.filters, params.advanced],
    queryFn: async () => {
      const { error, count } = await narrowed({ count: "exact", head: true });
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? null;
    },
  });

  const rows = query.data ?? [];
  const enrichment = useEnrichmentForIds(
    session.idToken, "person",
    rows.map((r) => r.id).filter((v): v is string => Boolean(v)),
  );

  const remember = (id: string) => rememberListPlace(placeKey, { scrollY: window.scrollY, openId: id });
  const open = (p: Person) => {
    if (!p.id) return;
    remember(p.id);
    navigate(`/people/${p.id}`, { state: { from: "people" } });
  };

  useEffect(() => {
    if (restored.current || !rows.length) return;
    restored.current = true;
    const place = recallListPlace(placeKey);
    if (!place) return;
    setLastOpened(place.openId);
    window.scrollTo({ top: place.scrollY });
  }, [rows.length, placeKey]);

  // The older ?record= form, which links elsewhere still use, opens the page.
  if (params.openRecord) return <Navigate to={`/people/${params.openRecord}`} replace />;

  const columns: ColumnSpec<Person>[] = [
    { id: "full_name", header: "Name", role: "leading", fields: ["full_name"], sort: "full_name", narrowLabel: null, title: true,
      /* A real link, so a rep can open five people into tabs with a modifier
         key, which a button cannot do. The row's own click leaves links to
         themselves (src/ui/DataTable.tsx). */
      cell: (p) => p.id ? (
        <Link to={`/people/${p.id}`} state={{ from: "people" }} className="link" onClick={() => remember(p.id as string)}>{p.full_name}</Link>
      ) : p.full_name },
    { id: "title", header: "Title", role: "leading", fields: ["title"], sort: "title", narrowLabel: null, cell: (p) => orAbsent(p.title) },
    { id: "employer_name", header: "Employer", role: "secondary", fields: ["employer_name"], sort: "employer_name", narrowLabel: null,
      cell: (p) => <RecordLink to="companies" id={p.employer_id} label={p.employer_name} /> },
    { id: "level", header: "Level", role: "secondary", fields: ["level"], sort: "level", narrowLabel: null, cell: (p) => orAbsent(levelWord(p.level)) },
    { id: "job_function", header: "Function", role: "secondary", fields: ["job_function"], sort: "job_function", narrowLabel: null,
      cell: (p) => <FunctionWord fn={p.job_function} /> },
    { id: "reach", header: "Reach", role: "secondary", fields: ["has_mobile", "has_email", "has_phone", "has_linkedin"], narrowLabel: null,
      cell: (p) => reachWordsFrom(p) ? <span className={p.has_mobile || p.has_email || p.has_linkedin ? "" : "muted"}>{reachWordsFrom(p)}</span> : absent() },
    { id: "standing", header: "Standing", role: "secondary", fields: ["standing"], sort: "standing", narrowLabel: null,
      cell: (p) => <StandingWord standing={p.standing} /> },
    { id: "source_count", header: "Sources", role: "secondary", fields: ["source_count"], align: "num", sort: "source_count",
      cell: (p) => p.source_count ?? absent() },
    { id: "hubspot_status", header: "In HubSpot", role: "detail", fields: ["hubspot_status"], sort: "hubspot_status",
      cell: (p) => p.hubspot_status === "yes" ? "yes"
        : nothingUseful(p.hubspot_status === "not checked" ? "Not checked against HubSpot yet" : "Not found in HubSpot") },
    { id: "enrichment", header: "Enrichment", role: "secondary", fields: [], narrowLabel: null,
      cell: (p) => <EnrichmentBadge state={p.id ? enrichment.data?.[p.id]?.state : null} compact /> },
  ];

  return (
    <ListShell
      title="People"
      view="person"
      params={params}
      csvColumns={csvColumns}
      total={countQuery.data ?? null}
      totalPending={countQuery.isPending}
      rowCount={rows.length}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      isError={query.isError}
      error={query.error as Error | null}
      dataUpdatedAt={query.dataUpdatedAt}
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ["people"] })}
      onRetry={() => query.refetch()}
      onDownload={() =>
        downloadNarrowed({
          name: "people",
          columns: csvColumns.chosen,
          total: countQuery.data ?? null,
          fetchAll: async (ceiling) => {
            const { data, error } = await applySorts(narrowed(undefined, csvColumns.chosen.join(",")), params.sorts)
              .order("full_name", { ascending: true })
              .range(0, ceiling - 1);
            if (error) throw new Error(apiErrorMessage(error));
            return (data ?? []) as Person[];
          },
        })
      }
      searchLabel="Search by name"
      emptyEverything="No people match these filters. Change them above, or press Refresh."
      filterLabels={{ level: "Level", reach: "Reach", hubspot: "HubSpot" }}
      filters={
        <>
          <NativeSelect
            id="f-level" label="Level" value={params.filters["level"] ?? ""}
            onChange={(e) => params.set({ filters: { ...params.filters, level: e.currentTarget.value } })}
            data={[{ value: "", label: "any" }, ...Object.entries(LEVEL_WORDS).map(([value, label]) => ({ value, label }))]}
          />
          <NativeSelect
            id="f-reach" label="Reach" value={params.filters["reach"] ?? ""}
            onChange={(e) => params.set({ filters: { ...params.filters, reach: e.currentTarget.value } })}
            data={[{ value: "", label: "any" }, { value: "mobile", label: "has a mobile" }, { value: "none", label: "no contact details" }]}
          />
          <NativeSelect
            id="f-hubspot" label="HubSpot" value={params.filters["hubspot"] ?? ""}
            onChange={(e) => params.set({ filters: { ...params.filters, hubspot: e.currentTarget.value } })}
            data={[{ value: "", label: "any" }, { value: "yes", label: "yes" }, { value: "no", label: "no" }, { value: "not checked", label: "not checked" }]}
          />
        </>
      }
    >
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(p) => p.id ?? ""}
        params={params}
        visible={visibleWith(csvColumns.chosen)}
        openId={lastOpened}
        onOpen={open}
        rowMenu={(p) => (
          <RowMenu items={[
            { label: "Run web enrichment", title: "Search the web for this record", onSelect: () => setRowOpen({ id: p.id ?? null, action: "enrich" }) },
            { label: "Flag", title: "Say this record is wrong. The record stays as it is.", onSelect: () => setRowOpen({ id: p.id ?? null, action: "flag" }) },
          ]} />
        )}
        rowExtra={(p) => rowOpen.id === p.id && rowOpen.action ? (
          <RowActionForm token={session.idToken} entityType="person" entityId={p.id as string}
                         action={rowOpen.action} onDone={() => setRowOpen({ id: null, action: null })} />
        ) : null}
      />
    </ListShell>
  );
}
